import React, { useEffect, useState, useRef } from "react";
import {
  Dialog,
  DialogTitle,
  DialogContent,
  IconButton,
  useMediaQuery,
  useTheme,
  Box,
  Typography,
  CircularProgress,
  TextField,
  Button,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Divider,
  Autocomplete,
} from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import AddIcon from "@mui/icons-material/Add";
import EditIcon from "@mui/icons-material/Edit";
import SaveIcon from "@mui/icons-material/Save";
import CancelIcon from "@mui/icons-material/Cancel";
import { DataGrid } from "@mui/x-data-grid";
import Swal from "sweetalert2";

import { tokens } from "../theme";
import {
  fetchDataEngine,
  generateAndExecuteMutation,
} from "../data/Axios/DynamicService";

/**
 * DynamicLinesDialog - A reusable, configuryation-driven dialog for managing line items
 *
 * @param {boolean} open - Whether the dialog is open
 * @param {function} onClose - Callback when dialog closes
 * @param {string} title - Dialog title
 * @param {object} parentData - Parent entity data (e.g., claimData, imprestData)
 * @param {string} parentIdField - Field name for parent ID in queries/mutations
 * @param {string} baseUrl - API base URL
 * @param {object} queryConfig - Query configuration { name, modelName, inputField, fields }
 * @param {object} mutationConfig - Mutation configuration { create, update, modelName }
 * @param {array} formFields - Form field configurations
 * @param {array} columns - Table column configurations
 * @param {object} optionsLoaders - Functions to load options for select fields
 * @param {boolean} canAdd - Whether adding new lines is allowed (default: true)
 * @param {object} readOnlyInfo - Info to display in edit-only mode { fields: [{label, getValue}] }
 */
const DynamicLinesDialog = ({
  open,
  onClose,
  title = "Manage Lines",
  parentData,
  parentIdField = "id",
  baseUrl,
  queryConfig,
  mutationConfig,
  refetchData,
  formFields = [],
  columns = [],
  optionsLoaders = {},
  canAdd = true,
  readOnlyInfo = null,
  additionalMutationData = {},
  skipAuditFields = false,
  customCalculations = {},
  headerActions = null,
  transformSavedRow = null, // optional callback to massage a row after mutation
  onSaveSuccess = null, // optional callback after successful save [new]
}) => {
  const theme = useTheme();
  const colors = tokens(theme.palette.mode);
  const fullScreen = useMediaQuery(theme.breakpoints.down("sm"));

  // ================= UI STATE =================
  const [showForm, setShowForm] = useState(false);

  // ================= DATA STATE =================
  const [lines, setLines] = useState([]);
  const [loading, setLoading] = useState(false);
  const [submitting, setSubmitting] = useState(false);

  // ================= FORM STATE =================
  const [formValues, setFormValues] = useState({});
  const [fieldOptions, setFieldOptions] = useState({});

  // ================= EDIT STATE =================
  const [isEditing, setIsEditing] = useState(false);
  const [editingLineId, setEditingLineId] = useState(null);
  const [editingRow, setEditingRow] = useState(null);
  const loadedInitialOptionsRef = useRef({});
  const loadingInitialOptionsRef = useRef({});

  const decodedToken = JSON.parse(localStorage.getItem("decodedToken") || "{}");
  const userId = Number(decodedToken.Id) || null;

  // ================= FORMATTING HELPERS =================
  const formatAmount = (val) => {
    if (val === null || val === undefined || val === "") return "";
    const str = val.toString().replace(/,/g, "");
    if (str === "" || isNaN(str)) return "";
    const parts = str.split(".");
    parts[0] = parts[0].replace(/\B(?=(\d{3})+(?!\d))/g, ",");
    return parts.join(".");
  };

  // ================= TABLE COLUMNS WITH ACTIONS =================
  const tableColumns = [
    ...columns.map((col) => ({
      ...col,
      valueFormatter:
        col.format === "currency"
          ? (params) =>
            params.value != null
              ? new Intl.NumberFormat("en-KE", {
                style: "currency",
                currency: "KES",
              }).format(params.value)
              : "-"
          : col.valueFormatter,
    })),
    {
      field: "actions",
      headerName: "Actions",
      flex: 0.8,
      sortable: false,
      renderCell: (params) => (
        <Button
          size="small"
          startIcon={<EditIcon />}
          onClick={() => handleEditLine(params.row)}
          sx={{
            backgroundColor: theme.palette.mode === "light" ? "#1766A2" : colors.blueAccent[600],
            color: theme.palette.mode === "light" ? "#ffffff" : colors.grey[100],
            "&:hover": {
              backgroundColor: theme.palette.mode === "light" ? "#125181" : colors.blueAccent[700],
            },
          }}
        >
          Edit
        </Button>
      ),
    },
  ];

  // ================= EFFECTS =================
  // Load initial options for non-dependent select fields
  useEffect(() => {
    if (!open) return;

    const loadInitialOptions = async () => {
      for (const field of formFields) {
        if (field.type === "select" && optionsLoaders[field.name]) {
          const cacheKey = `${parentData?.id ?? "global"}:${field.name}`;
          if (
            loadedInitialOptionsRef.current[cacheKey] ||
            loadingInitialOptionsRef.current[cacheKey]
          ) {
            continue;
          }

          try {
            loadingInitialOptionsRef.current[cacheKey] = true;
            const options = await optionsLoaders[field.name]();
            setFieldOptions((prev) => ({ ...prev, [field.name]: options }));
            loadedInitialOptionsRef.current[cacheKey] = true;
          } catch (e) {
            console.error(`Error loading options for ${field.name}:`, e);
          } finally {
            delete loadingInitialOptionsRef.current[cacheKey];
          }
        }
      }
    };
    loadInitialOptions();
  }, [open, parentData?.id, formFields, optionsLoaders]);

  useEffect(() => {
    if (!open) {
      loadedInitialOptionsRef.current = {};
      loadingInitialOptionsRef.current = {};
    }
  }, [open]);

  useEffect(() => {
    if (open && parentData?.id) {
      fetchLines();
      resetForm();
      setShowForm(false);
    }
  }, [open, parentData?.id]);

  // Enrich fetched lines with display names once options are loaded.
  // We watch BOTH fieldOptions and lines so the enrichment runs regardless
  // of which async operation (fetchLines vs loadInitialOptions) finishes first.
  useEffect(() => {
    if (transformSavedRow && Object.keys(fieldOptions).length > 0) {
      setLines((prev) => {
        if (prev.length === 0) return prev;
        const enriched = prev.map((row) => transformSavedRow(row));
        // Only update if something actually changed to avoid unnecessary re-renders
        const changed = enriched.some(
          (row, i) => JSON.stringify(row) !== JSON.stringify(prev[i])
        );
        return changed ? enriched : prev;
      });
    }
  }, [fieldOptions, lines.length, transformSavedRow]);

  // ================= FUNCTIONS =================
  const fetchLines = async () => {
    if (!queryConfig) return;

    setLoading(true);
    try {
      const inputData = { [queryConfig.inputField]: Number(parentData.id) };
      const result = await fetchDataEngine(
        queryConfig.name,
        queryConfig.modelName,
        inputData,
        queryConfig.fields,
        baseUrl,
      );
      setLines(result || []);
    } catch (e) {
      console.error("Error fetching lines:", e);
      setLines([]);
    } finally {
      setLoading(false);
    }
  };

  const resetForm = () => {
    const initialValues = {};
    formFields.forEach((field) => {
      initialValues[field.name] = field.defaultValue ?? "";
    });
    setFormValues(initialValues);
    setIsEditing(false);
    setEditingLineId(null);
    setEditingRow(null);
  };

  const handleShowAddForm = () => {
    resetForm();
    setShowForm(true);
  };

  const handleFieldChange = async (fieldName, value) => {
    let updatedValues = { ...formValues, [fieldName]: value };
    setFormValues(updatedValues);

    const fieldConfig = formFields.find((f) => f.name === fieldName);

    if (
      fieldConfig?.onChangeEffect &&
      typeof customCalculations[fieldConfig.onChangeEffect] === "function"
    ) {
      const result = customCalculations[fieldConfig.onChangeEffect](
        updatedValues,
        fieldOptions,
      );

      if (result && typeof result === "object") {
        updatedValues = { ...updatedValues, ...result };
        setFormValues(updatedValues);
      }
    }

    const dependentFields = formFields.filter((f) => f.dependsOn === fieldName);

    for (const depField of dependentFields) {
      updatedValues = { ...updatedValues, [depField.name]: "" };
      setFormValues(updatedValues);

      if (optionsLoaders[depField.name]) {
        try {
          const options = await optionsLoaders[depField.name](value);
          setFieldOptions((prev) => ({
            ...prev,
            [depField.name]: options,
          }));
        } catch (e) {
          console.error(`Error loading options for ${depField.name}:`, e);
        }
      }
    }
  };

  const handleSaveLine = async () => {
    // Validate required fields
    for (const field of formFields) {
      if (
        field.required !== false &&
        !formValues[field.name] &&
        formValues[field.name] !== 0
      ) {
        Swal.fire("Missing fields", `Please fill in ${field.label}`, "warning");
        return;
      }
    }

    setSubmitting(true);
    try {
      const payload = {
        [parentIdField]: parentData.id,
        isActive: 1,
        ...additionalMutationData,
      };

      // Add form values to payload
      formFields.forEach((field) => {
        // Skip file fields as they are handled separately via upload endpoints
        if (field.type === "file") {
          return;
        }

        if (field.type === "number") {
          // Strip commas before converting to number
          const raw = String(formValues[field.name]).replace(/,/g, "");
          payload[field.name] = parseFloat(raw) || 0;
        } else if (
          field.type === "select" ||
          field.type === "dependent-select"
        ) {
          payload[field.name] =
            Number(formValues[field.name]) || formValues[field.name];
        } else {
          payload[field.name] = formValues[field.name];
        }
      });

      if (!skipAuditFields) {
        if (isEditing) {
          payload.id = editingLineId;
          payload.updatedBy = userId;
        } else {
          payload.createdBy = userId;
        }
      } else {
        if (isEditing) {
          payload.id = editingLineId;
        }
      }

      const mutationName = isEditing
        ? mutationConfig.update
        : mutationConfig.create;
      const mutationModelName = isEditing
        ? mutationConfig.updateModelName || mutationConfig.modelName
        : mutationConfig.modelName;
      const mutationResponse = await generateAndExecuteMutation(
        mutationName,
        payload,
        mutationModelName,
        baseUrl,
      );

      // mutationResponse is typically { [mutationName]: row }
      let newRow =
        mutationResponse && mutationResponse[mutationName]
          ? mutationResponse[mutationName]
          : mutationResponse;

      // allow parent to modify row before we add/update it
      if (transformSavedRow && typeof transformSavedRow === "function") {
        newRow = transformSavedRow(newRow);
      }

      // ensure we have an id property for DataGrid
      if (
        newRow &&
        newRow.id == null &&
        newRow[mutationName] &&
        newRow[mutationName].id
      ) {
        newRow = newRow[mutationName];
      }

      // Re-fetch lines from server to get complete data with relational names
      await fetchLines();

      Swal.fire({
        title: "Success",
        text: "Line saved successfully",
        icon: "success",
        backdrop: true,
        allowOutsideClick: false,
        didOpen: () => {
          const container = Swal.getContainer();
          if (container) container.style.setProperty("z-index", "99999", "important");
        },
      });
      resetForm();
      if (typeof refetchData === "function") refetchData();
      if (typeof onSaveSuccess === "function") onSaveSuccess(newRow, formValues);
      setShowForm(false);
    } catch (e) {
      console.error("Error saving line:", e);
      Swal.fire("Error", "Failed to save line", "error");
    } finally {
      setSubmitting(false);
    }
  };

  const handleEditLine = async (row) => {
    setIsEditing(true);
    setEditingLineId(row.id);
    setEditingRow(row);

    // Populate form values from row
    const values = {};
    for (const field of formFields) {
      // Format number fields with commas for display
      if (field.type === "number") {
        values[field.name] = formatAmount(row[field.name]);
      } else {
        values[field.name] = row[field.name] ?? "";
      }

      // Load options for dependent fields
      if (
        field.dependsOn &&
        row[field.dependsOn] &&
        optionsLoaders[field.name]
      ) {
        try {
          const options = await optionsLoaders[field.name](
            row[field.dependsOn],
          );
          setFieldOptions((prev) => ({ ...prev, [field.name]: options }));
        } catch (e) {
          console.error(`Error loading options for ${field.name}:`, e);
        }
      }
    }
    setFormValues(values);
    setShowForm(true);
  };

  const handleClose = () => {
    resetForm();
    setShowForm(false);
    onClose();
  };

  // ================= FIELD RENDERER =================
  const renderFormField = (field) => {
    const commonSx = {
      "& .MuiFilledInput-root": {
        backgroundColor: theme.palette.mode === "light" ? "#f3f4f6" : colors.primary[500],
        color: theme.palette.mode === "light" ? "#1a1a2e" : colors.grey[100],
      },
      "& .MuiInputLabel-root": {
        color: theme.palette.mode === "light" ? "#1a1a2e" : colors.grey[300],
      },
      "& .MuiSelect-icon": {
        color: theme.palette.mode === "light" ? "#1a1a2e" : colors.grey[300],
      }
    };

    switch (field.type) {
      case "file":
        return (
          <Box key={field.name} sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <Button
              variant="outlined"
              component="label"
              sx={{ height: '100%', borderColor: theme.palette.mode === "light" ? "#ccc" : colors.grey[500],
                    color: theme.palette.mode === "light" ? "#333" : colors.grey[100] }}
            >
              Upload Document
              <input
                type="file"
                hidden
                multiple={field.multiple}
                accept={field.accept || "*/*"}
                onChange={(e) => {
                  if (field.multiple) {
                    handleFieldChange(field.name, Array.from(e.target.files));
                  } else {
                    handleFieldChange(field.name, e.target.files[0]);
                  }
                }}
                disabled={field.disabled}
              />
            </Button>
            {formValues[field.name] && (
              <Typography variant="body2" color={colors.grey[300]} sx={{ overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap", maxWidth: 150 }}>
                {field.multiple 
                  ? `${formValues[field.name].length} file(s) selected` 
                  : (formValues[field.name].name || "File selected")}
              </Typography>
            )}
          </Box>
        );

      case "text":
        return (
          <TextField
            key={field.name}
            label={field.label}
            variant="filled"
            value={formValues[field.name] || ""}
            onChange={(e) => handleFieldChange(field.name, e.target.value)}
            disabled={field.disabled}
            sx={commonSx}
            fullWidth
          />
        );

     case "number":
  return (
    <TextField
      key={field.name}
      label={field.label}
      inputMode="decimal"
      variant="filled"
      value={formatAmount(formValues[field.name])}
      onChange={(e) => {
        const raw = e.target.value.replace(/,/g, "");

        if (raw === "" || /^[0-9]*\.?[0-9]*$/.test(raw)) {
          handleFieldChange(field.name, raw);
        }
      }}
      disabled={field.disabled}
      sx={commonSx}
      fullWidth
    />
  );

      case "select":
      case "dependent-select":
        const options = fieldOptions[field.name] || field.options || [];
        const isDisabled =
          field.disabled || (field.dependsOn && !formValues[field.dependsOn]);

        return (
          <FormControl
            key={field.name}
            fullWidth
            variant="filled"
            disabled={isDisabled}
            sx={commonSx}
          >
            <InputLabel>{field.label}</InputLabel>
            <Select
              value={formValues[field.name] || ""}
              onChange={(e) => handleFieldChange(field.name, e.target.value)}
            >
              {options.map((opt) => (
                <MenuItem key={opt.id || opt.value} value={opt.id || opt.value}>
                  {opt.label ||
                    opt.name ||
                    opt.accountSubCategoryName ||
                    opt.accountCategoryName ||
                    opt.accountName}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
        );

      case "autocomplete":
        const acOptions = fieldOptions[field.name] || field.options || [];
        return (
          <Autocomplete
            key={field.name}
            fullWidth
            options={acOptions}
            getOptionLabel={(o) => o.label || o.name || ""}
            value={
              acOptions.find((o) => o.id === formValues[field.name]) || null
            }
            onChange={(e, v) => handleFieldChange(field.name, v?.id || null)}
            renderInput={(params) => (
              <TextField
                {...params}
                variant="filled"
                label={field.label}
                sx={commonSx}
              />
            )}
          />
        );

      case "date":
        return (
          <TextField
            key={field.name}
            label={field.label}
            type="date"
            variant="filled"
            value={formValues[field.name] || ""}
            onChange={(e) => handleFieldChange(field.name, e.target.value)}
            disabled={field.disabled}
            InputLabelProps={{ shrink: true }}
            sx={commonSx}
            fullWidth
          />
        );

      default:
        return null;
    }
  };

  // ================= RENDER =================
  return (
    <Dialog
      open={open}
      onClose={handleClose}
      fullWidth
      maxWidth="md"
      fullScreen={fullScreen}
      PaperProps={{
        sx: {
          backgroundColor: colors.primary[400],
          borderRadius: "12px",
        },
      }}
    >
      <DialogTitle
        sx={{
          backgroundColor: theme.palette.mode === "light" ? "#eff6ff" : colors.blueAccent[700],
          color: theme.palette.mode === "light" ? "#1766A2" : colors.grey[100],
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center", // Align items vertically
        }}
      >
        <Box display="flex" alignItems="center" gap={2}>
          {title}
        </Box>

        <Box display="flex" alignItems="center" gap={1}>
          {headerActions && <Box>{headerActions}</Box>}
          <IconButton onClick={handleClose} sx={{ color: theme.palette.mode === "light" ? "#1766A2" : colors.grey[100] }}>
            <CloseIcon />
          </IconButton>
        </Box>
      </DialogTitle>

      <DialogContent dividers sx={{ backgroundColor: colors.primary[400] }}>
        {/* ADD BUTTON */}
        {!showForm && canAdd && (
          <Box display="flex" justifyContent="flex-end" mb={2}>
            <Button
              startIcon={<AddIcon />}
              onClick={handleShowAddForm}
              sx={{
                backgroundColor: colors.greenAccent[600],
                color: colors.grey[100],
                "&:hover": {
                  backgroundColor: colors.greenAccent[700],
                },
              }}
            >
              Add Line
            </Button>
          </Box>
        )}

        {/* FORM */}
        {showForm && (
          <Box mb={3}>
            <Typography variant="h6" mb={2} color={colors.grey[100]}>
              {isEditing ? "Edit Line" : "Add Line"}
            </Typography>

            {/* Read-only info for edit mode */}
            {isEditing && readOnlyInfo && editingRow && (
              <Box
                sx={{
                  p: 2,
                  mb: 2,
                  backgroundColor: colors.primary[500],
                  borderRadius: 1,
                }}
              >
                {readOnlyInfo.fields.map((info, idx) => (
                  <Typography
                    key={idx}
                    variant="body2"
                    color={colors.grey[300]}
                    mt={idx > 0 ? 1 : 0}
                  >
                    <strong>{info.label}:</strong> {info.getValue(editingRow)}
                  </Typography>
                ))}
              </Box>
            )}

            <Box display="grid" gridTemplateColumns="repeat(4,1fr)" gap={2}>
              {formFields.map(renderFormField)}
            </Box>

            <Box mt={2} display="flex" gap={2}>
              <Button
                startIcon={<SaveIcon />}
                onClick={handleSaveLine}
                disabled={submitting}
                sx={{
                  backgroundColor: colors.greenAccent[600],
                  color: colors.grey[100],
                  "&:hover": {
                    backgroundColor: colors.greenAccent[700],
                  },
                }}
              >
                {isEditing ? "Update Line" : "Save Line"}
              </Button>

              <Button
                startIcon={<CancelIcon />}
                variant="outlined"
                onClick={() => {
                  resetForm();
                  setShowForm(false);
                }}
                sx={{
                  color: colors.grey[100],
                  borderColor: colors.grey[500],
                }}
              >
                Cancel
              </Button>
            </Box>
          </Box>
        )}

        {/* TABLE */}
        {!showForm && (
          <>
            <Divider sx={{ my: 2 }} />
            {loading ? (
              <Box display="flex" justifyContent="center" py={4}>
                <CircularProgress sx={{ color: colors.greenAccent[500] }} />
              </Box>
            ) : lines.length > 0 ? (
              <DataGrid
                rows={lines}
                columns={tableColumns}
                autoHeight
                pageSize={5}
                getRowId={(row) => row.id}
                sx={{
                  border: "none",
                  "& .MuiDataGrid-columnHeaders": {
                    backgroundColor: theme.palette.mode === "light" ? "#eff6ff" : colors.blueAccent[700],
                    color: theme.palette.mode === "light" ? "#1766A2" : colors.grey[100],
                  },
                  "& .MuiDataGrid-virtualScroller": {
                    backgroundColor: theme.palette.mode === "light" ? "#ffffff" : colors.primary[400],
                  },
                  "& .MuiDataGrid-footerContainer": {
                    backgroundColor: theme.palette.mode === "light" ? "#eff6ff" : colors.blueAccent[700],
                    color: theme.palette.mode === "light" ? "#1766A2" : colors.grey[100],
                  },
                  "& .MuiDataGrid-cell": {
                    borderBottom: theme.palette.mode === "light" ? "1px solid #e0e4e7" : `1px solid ${colors.grey[700]}`,
                    color: theme.palette.mode === "light" ? "#1a1a2e" : undefined,
                  },
                }}
              />
            ) : (
              <Box
                display="flex"
                justifyContent="center"
                alignItems="center"
                py={4}
              >
                <Typography color="textSecondary">
                  No lines found.{" "}
                  {canAdd ? "Click 'Add Line' to create one." : ""}
                </Typography>
              </Box>
            )}
          </>
        )}
      </DialogContent>
    </Dialog>
  );
};

export default DynamicLinesDialog;
